import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { faCopy, faCheck, faRotateRight, faHammer } from '@fortawesome/free-solid-svg-icons';
import Hero from '../../common/layout/Hero';
import HeroBody from '../../common/layout/HeroBody';
import Container from '../../common/layout/Container';
import Icon from '../../common/elements/Icon';
import Button from '../../common/elements/Button';
import SmallLoadingSpinner from '../../common/elements/SmallLoadingSpinner';
import { thor_api_urls } from '../../common/Constants';
import '../css/thor.css';

// Hyphenated (not underscored) on purpose — some proxies between Amplify and thor-api silently
// drop headers with underscores in the name (e.g. NGINX's default underscores_in_headers off),
// which caused 403s in deployment even though the browser sent the header correctly. thor-api's
// interceptor accepts both names during rollout, but the client only needs to send one.
const HAMMER_HEADER = 'SEFNTUVSLUFQSS1TRUNSRVQ=';

async function hammerFetch(method, url, body) {
    const opts = {
        method,
        headers: { 'URL-IDENTIFIER': HAMMER_HEADER, 'Content-Type': 'application/json' },
    };
    if (body !== undefined) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`${res.status}: ${text}`);
    }
    if (res.status === 204) return null;
    return res.json().catch(() => null);
}

function guessEnvironment() {
    const host = window.location.hostname;
    if (host === 'www.tripadvisor.com') return 'PROD';
    return 'DEV';
}

function readHashParams() {
    const hash = window.location.hash.replace('#', '');
    const params = new URLSearchParams(hash);
    return {
        taUnique: params.get('taUnique') ?? '',
        userId: params.get('userId') ?? '',
        // bucketIds may be comma- or plus-separated
        bucketIds: params.get('bucketIds') ?? '',
    };
}

// Given the raw state string (comma or plus separated) return a Set of integer bucket IDs
function parseBucketIds(raw) {
    if (!raw.trim()) return new Set();
    return new Set(
        raw.split(/[,+]/).map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
    );
}

// Port of Retool's flattenExperiment — enriches each entry with prebucket info
function flattenExperiment(obj, selectedBucketIds) {
    let prebucketKey = null;
    let prebucketId = null;
    (obj.bucketDetailsList ?? []).forEach(bucket => {
        if (selectedBucketIds.has(bucket.id)) {
            prebucketKey = bucket.key;
            prebucketId = bucket.id;
        }
    });
    return { ...obj, prebucketKey, prebucketId };
}

const ThorHammerContent = () => {
    const hashParams = readHashParams();
    const [taUnique, setTaUnique] = useState(hashParams.taUnique);
    const [userId, setUserId] = useState(hashParams.userId);
    const [bucketIds, setBucketIds] = useState(hashParams.bucketIds);
    const environment = guessEnvironment();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [experiments, setExperiments] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [editKey, setEditKey] = useState(null);
    const [editBucketId, setEditBucketId] = useState('');
    const [actionLoading, setActionLoading] = useState({});
    const [toast, setToast] = useState(null);
    const [stateCopied, setStateCopied] = useState(false);
    const [taCopied, setTaCopied] = useState(false);
    const [userCopied, setUserCopied] = useState(false);

    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 3500);
            return () => clearTimeout(t);
        }
    }, [toast]);

    const showToast = (message, type = 'info') => setToast({ message, type });

    // Parse bucketIds string → Set<int> for prebucket matching
    const selectedBucketIds = useMemo(() => parseBucketIds(bucketIds), [bucketIds]);

    // Enrich experiments with prebucket info derived from the user's current state
    const flattenedExperiments = useMemo(
        () => experiments.map(exp => flattenExperiment(exp, selectedBucketIds)),
        [experiments, selectedBucketIds]
    );

    const overrideCount = flattenedExperiments.filter(e => e.forcedBucketDetails != null).length;

    const filteredExperiments = flattenedExperiments.filter(exp => {
        if (!searchFilter.trim()) return true;
        const f = searchFilter.toLowerCase();
        return exp.experiment.key.toLowerCase().includes(f) ||
            exp.experiment.name.toLowerCase().includes(f) ||
            (exp.forcedBucketDetails?.bucketKey ?? '').toLowerCase().includes(f) ||
            (exp.prebucketKey ?? '').toLowerCase().includes(f);
    });

    const loadExperiments = useCallback(async (ta = taUnique, uid = userId, env = environment) => {
        setLoading(true);
        setError(null);
        try {
            const data = await hammerFetch(
                'GET',
                thor_api_urls.hammerExperiments(env, uid.trim(), ta.trim())
            );
            setExperiments(data?.experiments ?? []);
            setLoaded(true);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [taUnique, userId, environment]);

    // Auto-load on mount
    useEffect(() => {
        loadExperiments(hashParams.taUnique, hashParams.userId, guessEnvironment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upsertForcedBucket = async (experimentKey, bucketId) => {
        const id = parseInt(bucketId, 10);
        setActionLoading(prev => ({ ...prev, [experimentKey]: true }));
        try {
            await hammerFetch(
                'POST',
                thor_api_urls.hammerUpsertForcedBucket(experimentKey, id),
                { userId: userId.trim(), taUnique: taUnique.trim() }
            );
            setExperiments(prev => prev.map(e => {
                if (e.experiment.key !== experimentKey) return e;
                const bucket = (e.bucketDetailsList ?? []).find(b => b.id === id);
                return {
                    ...e,
                    forcedBucketDetails: {
                        bucketId: id,
                        bucketKey: bucket?.key ?? String(id),
                        experimentKey,
                        userId: userId.trim(),
                    }
                };
            }));
            setEditKey(null);
        } catch (e) {
            showToast(`Failed to set override: ${e.message}`, 'danger');
        } finally {
            setActionLoading(prev => ({ ...prev, [experimentKey]: false }));
        }
    };

    const deleteForcedBucket = async (experimentKey) => {
        setActionLoading(prev => ({ ...prev, [experimentKey]: true }));
        try {
            await hammerFetch(
                'DELETE',
                thor_api_urls.hammerDeleteForcedBucket(experimentKey),
                { userId: userId.trim(), taUnique: taUnique.trim() }
            );
            setExperiments(prev => prev.map(e =>
                e.experiment.key === experimentKey ? { ...e, forcedBucketDetails: null } : e
            ));
        } catch (e) {
            showToast(`Failed to clear override: ${e.message}`, 'danger');
        } finally {
            setActionLoading(prev => ({ ...prev, [experimentKey]: false }));
        }
    };

    const clearAll = async () => {
        if (!window.confirm(`Clear all ${overrideCount} override${overrideCount !== 1 ? 's' : ''}?`)) return;
        setLoading(true);
        try {
            await hammerFetch(
                'DELETE',
                thor_api_urls.hammerClearAllForcedBuckets(environment),
                { userId: userId.trim(), taUnique: taUnique.trim() }
            );
            setExperiments(prev => prev.map(e => ({ ...e, forcedBucketDetails: null })));
            showToast('All overrides cleared.', 'success');
        } catch (e) {
            showToast(`Failed to clear all: ${e.message}`, 'danger');
        } finally {
            setLoading(false);
        }
    };

    // Copy state: forced bucket takes priority, fall back to natural prebucket
    const copyState = () => {
        const state = flattenedExperiments
            .map(e => e.forcedBucketDetails?.bucketId ?? e.prebucketId)
            .filter(id => id != null)
            .join('+');

        if (!state) {
            showToast('No overrides found!', 'warning');
            return;
        }

        navigator.clipboard.writeText(state).then(() => {
            setStateCopied(true);
            setTimeout(() => setStateCopied(false), 2000);
        });
    };

    const copyToClipboard = (value, setCopiedFn) => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            setCopiedFn(true);
            setTimeout(() => setCopiedFn(false), 1500);
        });
    };

    return (
        <>
            <Container>
                <Hero>
                    <HeroBody>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <Icon icon={faHammer} />
                            <p className="title is-1">Hammer</p>
                        </div>
                        <p className="subtitle is-5 has-text-grey">Force experiment bucket overrides for testing.</p>
                    </HeroBody>
                </Hero>

                <div className="notification is-warning is-light py-2 px-4 mb-4" style={{ borderLeft: '4px solid #ffdd57' }}>
                    📣 Changes you make here can take <strong>up to 5 minutes</strong> to appear live.
                </div>

                {/* Identity + current state */}
                <div className="box mb-4">
                    <p className="subtitle is-6 mb-3 has-text-weight-semibold">Identity</p>
                    <div className="columns is-vcentered">
                        <div className="column">
                            <label className="label is-small">TA Unique / Device ID</label>
                            <div className="field has-addons">
                                <div className="control is-expanded">
                                    <input
                                        className="input is-small"
                                        type="text"
                                        placeholder="Optional — shows your forced overrides"
                                        value={taUnique}
                                        onChange={e => setTaUnique(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadExperiments()}
                                    />
                                </div>
                                <div className="control">
                                    <button type="button" className="button is-small is-light"
                                        title="Copy TA Unique" disabled={!taUnique}
                                        onClick={() => copyToClipboard(taUnique, setTaCopied)}>
                                        <Icon icon={taCopied ? faCheck : faCopy} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="column">
                            <label className="label is-small">User ID</label>
                            <div className="field has-addons">
                                <div className="control is-expanded">
                                    <input
                                        className="input is-small"
                                        type="text"
                                        placeholder="Optional"
                                        value={userId}
                                        onChange={e => setUserId(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadExperiments()}
                                    />
                                </div>
                                <div className="control">
                                    <button type="button" className="button is-small is-light"
                                        title="Copy User ID" disabled={!userId}
                                        onClick={() => copyToClipboard(userId, setUserCopied)}>
                                        <Icon icon={userCopied ? faCheck : faCopy} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="column is-narrow" style={{ paddingTop: '1.75rem' }}>
                            <Button className="is-link is-small" onClick={() => loadExperiments()} disabled={loading}>
                                {loading ? <SmallLoadingSpinner /> : <Icon icon={faRotateRight} />}
                                <span>Reload</span>
                            </Button>
                        </div>
                    </div>

                    {error && (
                        <div className="notification is-danger is-light py-2 px-3 mt-2 is-size-7">{error}</div>
                    )}
                </div>

                {loading && !loaded && (
                    <div className="has-text-centered py-6"><SmallLoadingSpinner /></div>
                )}

                {loaded && (
                    <>
                        <div className="level mb-3">
                            <div className="level-left">
                                <div className="level-item">
                                    <input
                                        className="input is-small"
                                        type="text"
                                        placeholder="Search experiments…"
                                        style={{ width: '300px' }}
                                        value={searchFilter}
                                        onChange={e => setSearchFilter(e.target.value)}
                                    />
                                </div>
                                <div className="level-item is-size-7 has-text-grey">
                                    {filteredExperiments.length} of {experiments.length} experiments
                                    {overrideCount > 0 && (
                                        <span className="ml-2 tag is-link is-light is-small">
                                            {overrideCount} override{overrideCount !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="table-container" style={{ marginBottom: '4.5rem' }}>
                            <table className="table is-fullwidth is-hoverable is-size-7">
                                <thead>
                                    <tr style={{ background: '#36e0a1' }}>
                                        <th style={{ color: '#000' }}>Experiment Name</th>
                                        <th style={{ color: '#000' }}>Experiment Key</th>
                                        <th style={{ color: '#000' }}>Dev</th>
                                        <th style={{ color: '#000' }}>Prod</th>
                                        <th style={{ color: '#000' }}>Bucket Key</th>
                                        <th style={{ color: '#000' }}>Override</th>
                                        <th style={{ color: '#000', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredExperiments.map(exp => {
                                        const key = exp.experiment.key;
                                        const forced = exp.forcedBucketDetails;
                                        const buckets = exp.bucketDetailsList ?? [];
                                        const isEditing = editKey === key;
                                        const isActing = actionLoading[key];

                                        return (
                                            <tr key={key}>
                                                <td style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {exp.experiment.name}
                                                </td>
                                                <td style={{ maxWidth: '190px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                                    <span title={key}>{key}</span>
                                                </td>
                                                <td>
                                                    <span className={`tag is-small ${exp.experiment.devStatus === 'RUNNING' ? 'is-success' : 'is-light'}`}>
                                                        {exp.experiment.devStatus ?? '–'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`tag is-small ${exp.experiment.prodStatus === 'RUNNING' ? 'is-success' : 'is-light'}`}>
                                                        {exp.experiment.prodStatus ?? '–'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {exp.prebucketKey
                                                        ? <span className="has-text-grey-dark">{exp.prebucketKey}</span>
                                                        : <span className="has-text-grey">–</span>}
                                                </td>
                                                <td style={{ minWidth: '200px' }}>
                                                    {isEditing ? (
                                                        <div className="field has-addons">
                                                            <div className="control">
                                                                <div className="select is-small">
                                                                    <select
                                                                        value={editBucketId}
                                                                        onChange={e => setEditBucketId(e.target.value)}
                                                                        autoFocus
                                                                    >
                                                                        <option value="">Select bucket…</option>
                                                                        {buckets.map(b => (
                                                                            <option key={b.id} value={b.id}>
                                                                                {b.key}{b.isControl ? ' (control)' : ''}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="control">
                                                                <button type="button" className="button is-small is-success"
                                                                    disabled={!editBucketId || isActing}
                                                                    onClick={() => upsertForcedBucket(key, editBucketId)}>
                                                                    {isActing ? <SmallLoadingSpinner /> : 'Save'}
                                                                </button>
                                                            </div>
                                                            <div className="control">
                                                                <button type="button" className="button is-small is-light"
                                                                    onClick={() => setEditKey(null)}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        forced
                                                            ? <span className="tag is-link is-light">{forced.bucketKey}</span>
                                                            : <span className="has-text-grey">–</span>
                                                    )}
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    {!isEditing && (
                                                        <>
                                                            <button type="button" className="button is-small is-light mr-1"
                                                                disabled={isActing}
                                                                onClick={() => { setEditKey(key); setEditBucketId(forced?.bucketId != null ? String(forced.bucketId) : ''); }}>
                                                                Edit
                                                            </button>
                                                            <button type="button" className="button is-small is-light"
                                                                disabled={!forced || isActing}
                                                                onClick={() => deleteForcedBucket(key)}>
                                                                {isActing ? <SmallLoadingSpinner /> : 'Clear'}
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {filteredExperiments.length === 0 && (
                                <div className="has-text-centered has-text-grey py-4 is-size-7">
                                    {searchFilter ? 'No experiments match your search.' : 'No experiments found.'}
                                </div>
                            )}
                        </div>

                        {/* Sticky bottom bar */}
                        <div style={{
                            position: 'fixed', bottom: 0, left: 0, right: 0,
                            background: '#fff', borderTop: '1px solid #dbdbdb',
                            padding: '0.75rem 1.5rem', display: 'flex',
                            justifyContent: 'flex-end', gap: '0.75rem', zIndex: 50,
                        }}>
                            <Button className="is-light" disabled={overrideCount === 0 || loading} onClick={clearAll}>
                                Clear all ({overrideCount})
                            </Button>
                            <Button className={stateCopied ? 'is-success' : 'is-dark'} onClick={copyState}>
                                <Icon icon={stateCopied ? faCheck : faCopy} />
                                <span>{stateCopied ? 'Copied!' : 'Copy state'}</span>
                            </Button>
                        </div>
                    </>
                )}
            </Container>

            {toast && (
                <div style={{ position: 'fixed', bottom: '4rem', right: '1.5rem', zIndex: 100, minWidth: '260px' }}
                    className={`notification is-${toast.type} is-light py-3 px-4`}>
                    <button className="delete" onClick={() => setToast(null)} />
                    {toast.message}
                </div>
            )}
        </>
    );
};

export const ThorHammerPage = () => <ThorHammerContent />;

export default ThorHammerPage;
